import type { ReactNode } from 'react'

import { cn } from '@/lib/utils.ts'

import { useHiddenFeature } from '@/components/useHiddenFeature.ts'
import { toRelativeTime } from '@/utils/date.ts'
import { requestNotificationPermission } from '@/utils/notification.ts'
import { identifyUsername } from '@/utils/string.ts'

type Props = {
  notifications: (ProjectNotification | SystemNotification)[]
  read: (id: string) => void
}

export const NotificationList = ({ notifications, read }: Props) => {
  const [mostRecentProjectNotification] = notifications.filter((notification) => notification.type !== 'SYSTEM')

  const hiddenFeatureRef = useHiddenFeature(requestNotificationPermission, 5)

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex flex-row justify-between items-center px-4">
        <div className="font-EN-KR-body1-bold font-contents-primary" ref={hiddenFeatureRef}>
          Notifications
        </div>
        <button
          type="button"
          className="cursor-pointer hover:bg-gray-100 p-1 rounded border border-border-default-neutral"
          onClick={() => mostRecentProjectNotification && read(mostRecentProjectNotification.id)}
        >
          <img src="/images/check.svg" alt="Mark all as read" className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-col w-full px-4 pb-2 max-h-[200px] overflow-y-scroll [&>*+*]:border-t [&>*+*]:border-tb-gray-100">
        {notifications.length > 0 ? (
          notifications.map((notification) =>
            notification.type !== 'SYSTEM' ? (
              <ProjectNotification key={notification.id} notification={notification} />
            ) : (
              <AppNotification key={notification.id} notification={notification} />
            ),
          )
        ) : (
          <div className="h-20 flex items-center justify-center">
            <span className="font-EN-KR-body2-regular text-tb-grey-700">You're all caught up</span>
          </div>
        )}
      </div>
    </div>
  )
}

type ProjectNotificationProps = {
  notification: ProjectNotification
}

const ProjectNotification = ({ notification }: ProjectNotificationProps) => {
  return (
    <div key={notification.id} className={cn('flex flex-row items-start gap-1 py-1 bg-surface-backgroundElevated')}>
      <div className="inline-flex items-center px-0 py-0.5 relative flex-[0_0_auto]">
        <div className="relative w-3.5 h-3.5">
          <img src="/images/mention-icon.svg" alt="mention icon" />
        </div>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <div className="flex flex-row justify-between items-center gap-1">
          <div
            className={cn('font-EN-KR-body2-bold text-contents-accent truncate', {
              'font-EN-KR-body2-regular text-contents-secondary': notification.readAt,
            })}
          >
            {notification.projectName}
          </div>
          <div className="font-EN-KR-caption-regular text-contents-secondary flex-shrink-0">
            {toRelativeTime(notification.createdAt)}
          </div>
        </div>
        <div
          className={cn('font-EN-KR-body2-regular text-contents-tertiary whitespace-pre-wrap break-words', {
            'text-contents-disabled': notification.readAt,
          })}
        >
          {notification.storyTitle}
        </div>
        <div className="font-EN-KR-caption-regular text-contents-primary whitespace-pre-wrap break-words">
          {highlightKeyword(notification.message, notification.id)}
        </div>
        <div className="font-EN-KR-caption-regular text-contents-primary whitespace-pre-wrap break-words">
          “{identifyUsername(notification.context)}”
        </div>
      </div>
    </div>
  )
}

const highlightKeyword = (text: string, notificationId: string): ReactNode => {
  const HIGHLIGHT_WORDS = ['accepted', 'rejected', 'mentioned'] as const
  const HIGHLIGHT_SET = new Set(HIGHLIGHT_WORDS.map((w) => w.toLowerCase()))

  const regex = new RegExp(`\\b(${HIGHLIGHT_WORDS.join('|')})\\b`, 'gi')

  const parts = text.split(regex)

  return parts.map((part, i) => {
    const isKeyword = HIGHLIGHT_SET.has(part.toLowerCase())

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: unique here
      <span key={`${notificationId}-${i}`} className={cn(isKeyword && 'font-EN-KR-caption-bold text-contents-accent')}>
        {part}
      </span>
    )
  })
}

type AppNotificationProps = {
  notification: SystemNotification
}

const AppNotification = ({ notification }: AppNotificationProps) => (
  <div key={notification.id} className={cn('flex flex-row items-start gap-1 py-1 bg-surface-backgroundElevated')}>
    <div className="inline-flex items-center px-0 py-0.5 relative flex-[0_0_auto]">
      <div className="relative w-3.5 h-3.5">
        <img src="/images/app-notification-icon.svg" alt="app noti icon" />
      </div>
    </div>
    <p className="font-EN-KR-body2-regular">{notification.message}</p>
  </div>
)
